import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { FileUpload } from '../../../../../../components/ui/FileUpload';
import { Upload, FileText, X, CheckCircle, AlertTriangle } from 'lucide-react';
import { Notification } from '../../../../../../components/ui/custom-notification';
import * as XLSX from 'xlsx';
import type { CoatingRow, ParsedCoatingFile } from './PumpHouseCoatingTable';
import { useCoatingPumphouse } from '../../../../../../redux/CoatingPumphouse/hooks/useCoatingPumphouse';
import coatingAxiosConfig from '../../../../../../redux/CoatingPumphouse/axiosConfig';
import { logPumpHouseCoatingUploaded } from '../../../../utils/productionAudit';
import { useProductionModalAudit } from '../../../../../audit/useProductionModalAudit';

interface UploadCoatingPumpHouseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: (data: ParsedCoatingFile) => void;
  setupData: any;
  project: any;
  selectedPipe: any;
}

// ── parser ────────────────────────────────────────────────────────────────────

const REQUIRED_META_KEYS = ['User', 'Coating Material', 'Coating Type', 'Base Batch', 'Hardener Batch'];

/**
 * Normalizes a time string from CSV/Excel to zero-padded `HH:mm:ss`.
 * Takes the first h:mm:ss run of digits (after normalizing `;` → `:`), so prefix/suffix text
 * such as `AM` / `PM` or labels does not affect the parsed numbers.
 */
function normalizeTime24HourHms(value: string): string | null {
  const s = String(value ?? '').trim().replace(/;/g, ':');
  if (!s) return null;
  const m = s.match(/(\d+)\s*:\s*(\d+)\s*:\s*(\d+)/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  const sec = Number(m[3]);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(min)}:${pad(sec)}`;
}

/** Zero-pads single-digit day/month in D.M.YYYY / D/MM/YYYY / D-MM-YYYY upload values. */
function normalizeUploadDate(value: string): string {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const m = s.match(/^(\d{1,2})([./-])(\d{1,2})\2(\d{4})$/);
  if (!m) return s;
  const day = m[1].padStart(2, '0');
  const month = m[3].padStart(2, '0');
  return `${day}${m[2]}${month}${m[2]}${m[4]}`;
}

interface ParseResult {
  file: ParsedCoatingFile | null;
  error: string | null;
}

const parseCsvText = (text: string, fileName: string, projectId: string | number, pipeId: string | number): ParseResult => {
  const rawLines = text.split(/\r?\n/).map(l => l.split(',').map(c => c.trim()));

  if (rawLines.length < 5) {
    return { file: null, error: 'File has too few rows. Expected metadata rows followed by a header row and data rows.' };
  }

  const metaKeys = rawLines[0] ?? [];
  const metaVals = rawLines[1] ?? [];

  if (metaKeys.every(k => k === '')) {
    return { file: null, error: 'Row 1 (metadata keys) is empty. This does not appear to be a valid coating export file.' };
  }

  const idx = (k: string) => metaKeys.findIndex(h => h.toLowerCase() === k.toLowerCase());

  const missingKeys = REQUIRED_META_KEYS.filter(k => idx(k) === -1);
  if (missingKeys.length > 0) {
    return { file: null, error: `Missing required metadata columns: ${missingKeys.join(', ')}` };
  }

  const metaForRow = {
    projectId,
    pipeId,
    user:             metaVals[idx('User')] ?? '',
    coatingMaterial:  metaVals[idx('Coating Material')] ?? '',
    coatingType:      metaVals[idx('Coating Type')] ?? '',
    baseBatch:        metaVals[idx('Base Batch')] ?? '',
    hardenerBatch:    metaVals[idx('Hardener Batch')] ?? '',
    jointWidth:       metaVals[idx('Joint Width')] ?? '',
    jointThickness:   metaVals[idx('Joint Thickness')] ?? '',
    prime:            metaVals[idx('Prime')] ?? '',
    flush:            metaVals[idx('Flush')] ?? '',
  };

  if (!metaForRow.user && !metaForRow.coatingMaterial) {
    return { file: null, error: 'Metadata row (row 2) appears empty. Please check the file format.' };
  }

  const rows: CoatingRow[] = [];
  for (let i = 4; i < rawLines.length; i++) {
    const cells = rawLines[i];
    if (!cells || cells.every(c => c === '')) continue;

    const timeCell = cells[1] ?? '';
    const timeForRow = normalizeTime24HourHms(timeCell) ?? String(timeCell).trim();
    const dateForRow = normalizeUploadDate(cells[0] ?? '');

    const isEvent =
      cells.length <= 4 &&
      cells[2] != null &&
      cells[2] !== '' &&
      isNaN(Number(cells[2])) &&
      cells[2].toLowerCase() !== 'pass' &&
      cells[2].toLowerCase() !== 'fail';

    if (isEvent) {
      rows.push({
        ...metaForRow,
        date: dateForRow, time: timeForRow, joint: '',
        waterTemp: '', baseTemp: '', hardenerTemp: '', ambientTemp: '',
        humidity: '', dewPoint: '', downstrokeStall: '', upstrokeStall: '',
        stallTestType: '', systemFlushed: '', systemPrimed: '',
        valveNumber: '', fittingNumber: '',
        isEvent: true, eventMessage: cells[2],
      });
    } else {
      rows.push({
        ...metaForRow,
        date: dateForRow, time: timeForRow, joint: cells[2] ?? '',
        waterTemp: cells[3] ?? '', baseTemp: cells[4] ?? '', hardenerTemp: cells[5] ?? '',
        ambientTemp: cells[6] ?? '', humidity: cells[7] ?? '', dewPoint: cells[8] ?? '',
        downstrokeStall: cells[9] ?? '', upstrokeStall: cells[10] ?? '',
        stallTestType: cells[11] ?? '', systemFlushed: cells[12] ?? '',
        systemPrimed: cells[13] ?? '', valveNumber: cells[14] ?? '', fittingNumber: cells[15] ?? '',
        isEvent: false,
      });
    }
  }

  if (rows.length === 0) {
    return { file: null, error: 'No data rows found after the header. The file may be empty or use an unsupported format.' };
  }

  return { file: { rows, fileName }, error: null };
};

// ── S3 upload helpers ─────────────────────────────────────────────────────────

/** First element of `getPreSignedUrlToUpload` → `data` (may include `s3Link` from API). */
interface PresignedData {
  presignedUrl: string;
  s3Key: string;
  fileName: string;
  s3Link?: string;
}

/** Value to send as `s3Link` on bulk save: API field, else HTTPS URL without query, else `s3Key`. */
function resolveS3LinkForBulkSave(p: PresignedData): string {
  const fromApi = typeof p.s3Link === 'string' ? p.s3Link.trim() : '';
  if (fromApi) return fromApi;
  const u = String(p.presignedUrl ?? '').trim();
  if (u.includes('?')) return u.split('?')[0];
  return String(p.s3Key ?? '').trim();
}

/**
 * Inserts a UTC timestamp before the file extension so every upload gets a
 * unique name and never overwrites an existing file.
 * e.g. "coating_data.csv" → "coating_data_20260414T093012Z.csv"
 */
function stampedFileName(name: string): string {
  const ts = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '').replace('T', 'T');
  const dot = name.lastIndexOf('.');
  return dot !== -1
    ? `${name.slice(0, dot)}_${ts}${name.slice(dot)}`
    : `${name}_${ts}`;
}

async function getPresignedUrl(
  file: File,
  projectId: string | number,
  pipeId: string | number,
): Promise<PresignedData | null> {
  try {
    const res = await coatingAxiosConfig.post('coatingPumphouse/getPreSignedUrlToUpload', [{
      fileName:    stampedFileName(file.name),
      contentType: file.type || 'application/octet-stream',
      projectId,
      pipeId,
      fileSize: file.size,
    }]);
    const row = res.data?.data?.[0];
    if (!row || typeof row !== 'object') return null;
    return row as PresignedData;
  } catch {
    return null;
  }
}

async function putFileToS3(presignedUrl: string, file: File): Promise<boolean> {
  try {
    const res = await axios.put(presignedUrl, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      timeout: 60_000,
    });
    return res.status === 200;
  } catch {
    return false;
  }
}

async function logCoatingFileUpload(payload: {
  fileName: string; s3Key: string;
  projectId: string | number; pipeId: string | number;
  projectName: string;
  uploadedBy: string; rowCount: number;
  validationStatus: 'pass' | 'fail';
  validationError?: string;
}): Promise<void> {
  try {
    await coatingAxiosConfig.post('coatingPumphouse/logCoatingFileUpload', payload);
  } catch {
    // Non-blocking — tracking failure must not disrupt the main UI flow
  }
}

// ── component ─────────────────────────────────────────────────────────────────

export const UploadCoatingPumpHouseDialog: React.FC<UploadCoatingPumpHouseDialogProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
  setupData,
  project,
  selectedPipe,
}) => {
  const projectId = project?.project?.projectId ?? project?.projectId;
  const projectName = project?.project?.projectName ?? project?.projectName ?? null;
  useProductionModalAudit(isOpen, 'Pump House Coating Upload', projectId, projectName);

  const authUser = useSelector((state: any) => state.auth?.user);

  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [parsed, setParsed] = useState<ParsedCoatingFile | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [s3Status, setS3Status] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  /** From `getPreSignedUrlToUpload` after successful S3 PUT — sent with `bulkCreateUpdateCoatingPumphouse`. */
  const [bulkSaveS3Link, setBulkSaveS3Link] = useState<string | null>(null);

  const {
    bulkSaveCoatingPumphouse,
    bulkSaveLoading,
    bulkSaveSuccess,
    bulkSaveError,
    clearBulkSaveCoatingPumphouse,
    getAllCoatingPumphouse,
  } = useCoatingPumphouse();

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setParsed(null);
      setValidationError(null);
      setIsUploading(false);
      setS3Status('idle');
      setBulkSaveS3Link(null);
      clearBulkSaveCoatingPumphouse();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Watch for API save result
  useEffect(() => {
    if (bulkSaveSuccess && parsed) {
      const projectId = project?.project?.projectId ?? project?.id ?? '';
      const pipeId = selectedPipe?.pipeId ?? selectedPipe?.id ?? '';
      const projName = project?.project?.projectName ?? project?.projectName ?? project?.name ?? '';
      const firstRow = parsed.rows[0];

      logPumpHouseCoatingUploaded({
        projectId,
        projectName: projName,
        pipeId,
        pipeName: selectedPipe?.displayName ?? selectedPipe?.pipeName ?? null,
        fileName: parsed.fileName,
        fileUrl: bulkSaveS3Link,
        rowCount: parsed.rows.length,
        coatingMaterial: firstRow?.coatingMaterial ?? null,
        coatingType: firstRow?.coatingType ?? null,
        baseBatch: firstRow?.baseBatch ?? null,
        hardenerBatch: firstRow?.hardenerBatch ?? null,
      });

      onUploadComplete?.(parsed);
      Notification.success('Coating data saved successfully');
      getAllCoatingPumphouse({ projectId, pipeId });
      setIsUploading(false);
      onClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkSaveSuccess]);

  useEffect(() => {
    if (bulkSaveError) {
      Notification.error(`Save failed: ${bulkSaveError}`);
      setIsUploading(false);
    }
  }, [bulkSaveError]);

  const processFile = async (f: File) => {
    setIsProcessing(true);
    setValidationError(null);
    setParsed(null);
    setS3Status('idle');
    setBulkSaveS3Link(null);
    try {
      const projId   = project?.project?.projectId ?? project?.id ?? '';
      const pId      = selectedPipe?.pipeId ?? selectedPipe?.id ?? '';
      const projName = project?.project?.projectName ?? project?.projectName ?? project?.name ?? '';
      let parseResult: ParseResult;

      if (f.name.endsWith('.csv') || f.type === 'text/csv') {
        const text = await f.text();
        parseResult = parseCsvText(text, f.name, projId, pId);
      } else {
        const buf = await f.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as string[][];
        const csvText = raw.map(r => r.map(c => String(c ?? '')).join(',')).join('\n');
        parseResult = parseCsvText(csvText, f.name, projId, pId);
      }

      // Update UI based on validation result
      if (parseResult.error) {
        setValidationError(parseResult.error);
        Notification.error(`Validation error: ${parseResult.error}`);
      } else if (parseResult.file) {
        setParsed(parseResult.file);
        Notification.success(`Parsed ${parseResult.file.rows.length} rows from ${f.name}`);
      }

      // Always upload to S3 and log — regardless of validation outcome.
      // Failed uploads are recorded with validationStatus='fail' so the backend
      // can track what was attempted and why it was rejected.
      setS3Status('uploading');
      const uploadedBy =
        authUser?.email || authUser?.sub ||
        JSON.parse(localStorage.getItem('user') || '{}')?.email ||
        'unknown';

      const presigned = await getPresignedUrl(f, projId, pId);
      if (presigned?.presignedUrl) {
        const s3Ok = await putFileToS3(presigned.presignedUrl, f);
        if (s3Ok) {
          const s3Link = resolveS3LinkForBulkSave(presigned);
          setBulkSaveS3Link(s3Link || null);
          await logCoatingFileUpload({
            fileName:         f.name,
            s3Key:            presigned.s3Key,
            projectId:        projId,
            pipeId:           pId,
            projectName:      projName,
            uploadedBy,
            rowCount:         parseResult.file?.rows.length ?? 0,
            validationStatus: parseResult.error ? 'fail' : 'pass',
            validationError:  parseResult.error ?? undefined,
          });
          setS3Status('done');
        } else {
          setBulkSaveS3Link(null);
          setS3Status('error');
        }
      } else {
        setBulkSaveS3Link(null);
        setS3Status('error');
      }
    } catch {
      Notification.error('Failed to parse file. Check the format and try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const f = files[0];
    const isCsv = f.name.endsWith('.csv') || f.type === 'text/csv';
    const isExcel =
      f.name.endsWith('.xlsx') || f.name.endsWith('.xls') ||
      f.type.includes('spreadsheet') || f.type.includes('excel');
    if (!isCsv && !isExcel) {
      Notification.error('Please upload a CSV or Excel (.xlsx / .xls) file');
      return;
    }
    setFile(f);
    processFile(f);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setParsed(null);
    setValidationError(null);
    setS3Status('idle');
    setBulkSaveS3Link(null);
  };

  const handleUpload = async () => {
    if (!parsed) return;
    if (!bulkSaveS3Link?.trim()) {
      Notification.error('Wait for the file to finish uploading to storage, then try again.');
      return;
    }
    setIsUploading(true);
    bulkSaveCoatingPumphouse({
      items: parsed.rows,
      s3Link: bulkSaveS3Link.trim(),
    });
  };

  const handleClose = () => { handleRemoveFile(); onClose(); };

  // Stats
  const passCnt  = parsed?.rows.filter(r => !r.isEvent && r.downstrokeStall.toLowerCase() === 'pass' && r.upstrokeStall.toLowerCase() === 'pass').length ?? 0;
  const failCnt  = parsed?.rows.filter(r => !r.isEvent && (r.downstrokeStall.toLowerCase() === 'fail' || r.upstrokeStall.toLowerCase() === 'fail')).length ?? 0;
  const eventCnt = parsed?.rows.filter(r => r.isEvent).length ?? 0;

  // First row carries the meta values shared across all rows
  const firstRow = parsed?.rows[0];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-[900px] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden bg-white">
        <div className="flex items-center gap-3 border-b border-[#E2E5EA] px-6 py-4 pr-14 flex-shrink-0">
          <div className="flex-shrink-0 w-10 h-10 bg-hero-red-10 rounded-full flex items-center justify-center">
            <Upload className="h-5 w-5 text-hero-red" />
          </div>
          <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
            Upload Pump House Coating Data
          </DialogTitle>
        </div>

        <div className="space-y-6 flex-1 overflow-y-auto overflow-x-hidden p-6">
          {/* Step 1 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-hero-red text-white rounded-full flex items-center justify-center text-[12px] font-medium">1</div>
              <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Upload CSV or Excel File</h3>
            </div>
            <p className="text-[13px] text-text-grey">
              Upload the coating machine export file (CSV or Excel). The file must have the standard
              pump house coating format with metadata header rows followed by data rows.
            </p>

            {file ? (
              <div className="flex items-center p-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
                <FileText className="h-4 w-4 text-heritage-grey mr-2 flex-shrink-0" />
                <span className="text-[13px] text-text-grey flex-1 truncate">{file.name}</span>
                {isProcessing && <span className="text-[12px] text-hero-red mr-2">Processing…</span>}
                <Button variant="ghost" size="sm" onClick={handleRemoveFile}
                  className="h-6 w-6 p-0 hover:bg-hero-red-10 hover:text-readable-red flex-shrink-0">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <FileUpload
                onFileSelect={handleFileSelect}
                accept=".csv,.xlsx,.xls"
                multiple={false}
                buttonText="Upload Coating Data File (CSV / Excel)"
                variant="outline"
                size="lg"
                className="w-full"
              />
            )}

            {/* Validation error banner */}
            {validationError && (
              <div className="flex items-start gap-3 p-3 bg-[rgba(196,0,31,0.06)] border border-hero-red-20 rounded-lg text-[13px] text-readable-red">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-readable-red" />
                <div>
                  <p className="font-medium">Invalid file format</p>
                  <p className="mt-0.5">{validationError}</p>
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Preview */}
          {parsed && !isProcessing && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-hero-red text-white rounded-full flex items-center justify-center text-[12px] font-medium">
                  <CheckCircle className="h-3.5 w-3.5" />
                </div>
                <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Preview</h3>
              </div>

              {/* Metadata from first row */}
              {firstRow && (
                <div className="bg-page-bg border border-[#E2E5EA] rounded-lg p-3 text-[12px]">
                  <div className="grid grid-cols-3 gap-x-6 gap-y-1">
                    {([
                      ['User',             firstRow.user],
                      ['Coating Material', firstRow.coatingMaterial],
                      ['Coating Type',     firstRow.coatingType],
                      ['Base Batch',       firstRow.baseBatch],
                      ['Hardener Batch',   firstRow.hardenerBatch],
                      ['Prime',            firstRow.prime],
                      ['Flush',            firstRow.flush],
                    ] as [string, string][]).map(([k, v]) => (
                      <div key={k}>
                        <span className="text-heritage-grey">{k}: </span>
                        <span className="font-medium text-text-grey">{v || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1 bg-page-bg border border-[#E2E5EA] rounded-full text-[12px] text-text-grey font-medium">Pass: {passCnt}</span>
                <span className="px-3 py-1 bg-[rgba(196,0,31,0.06)] border border-hero-red-20 rounded-full text-[12px] text-readable-red font-medium">Fail: {failCnt}</span>
                <span className="px-3 py-1 bg-hero-red-10 border border-hero-red-20 rounded-full text-[12px] text-readable-red font-medium flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />Events: {eventCnt}
                </span>
                <span className="px-3 py-1 bg-page-bg border border-[#E2E5EA] rounded-full text-[12px] text-text-grey font-medium">Total rows: {parsed.rows.length}</span>
              </div>

              {/* Mini preview table */}
              <div className="overflow-x-auto rounded-lg border border-[#E2E5EA] text-[12px]">
                <table className="min-w-full">
                  <thead className="sticky top-0 z-10 bg-page-bg shadow-[0_1px_0_0_#E2E5EA]">
                    <tr className="border-b border-[#E2E5EA]">
                      {['Date','Time','Joint','Water T.','Base T.','Hardener T.','Ambient T.','Humidity','Dew Pt','DS Stall','US Stall','Stall Type','Flushed','Primed'].map(h => (
                        <th key={h} className="px-2 py-1.5 text-left whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EEF0F3]">
                    {parsed.rows.filter(r => !r.isEvent).slice(0, 5).map((row, i) => (
                      <tr key={i} className="bg-white hover:bg-[#FAFBFC]">
                        <td className="px-2 py-1 whitespace-nowrap">{row.date}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{row.time}</td>
                        <td className="px-2 py-1 text-center">{row.joint}</td>
                        <td className="px-2 py-1 text-center">{row.waterTemp}</td>
                        <td className="px-2 py-1 text-center">{row.baseTemp}</td>
                        <td className="px-2 py-1 text-center">{row.hardenerTemp}</td>
                        <td className="px-2 py-1 text-center">{row.ambientTemp}</td>
                        <td className="px-2 py-1 text-center">{row.humidity}</td>
                        <td className="px-2 py-1 text-center">{row.dewPoint}</td>
                        <td className={`px-2 py-1 text-center font-medium ${row.downstrokeStall.toLowerCase() === 'pass' ? 'text-blueprint-blue' : row.downstrokeStall.toLowerCase() === 'fail' ? 'text-readable-red' : ''}`}>{row.downstrokeStall}</td>
                        <td className={`px-2 py-1 text-center font-medium ${row.upstrokeStall.toLowerCase() === 'pass' ? 'text-blueprint-blue' : row.upstrokeStall.toLowerCase() === 'fail' ? 'text-readable-red' : ''}`}>{row.upstrokeStall}</td>
                        <td className="px-2 py-1 text-center">{row.stallTestType}</td>
                        <td className="px-2 py-1 text-center">{row.systemFlushed}</td>
                        <td className="px-2 py-1 text-center">{row.systemPrimed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.rows.filter(r => !r.isEvent).length > 5 && (
                  <div className="px-3 py-2 text-heritage-grey text-[12px] text-center border-t border-[#EEF0F3]">
                    + {parsed.rows.filter(r => !r.isEvent).length - 5} more rows — click Load Data to view all
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 flex-shrink-0 border-t border-[#E2E5EA] bg-page-bg px-6 py-4">
          <Button variant="outline" onClick={handleClose} disabled={isUploading} className="min-w-[100px]">Cancel</Button>
          <Button
            onClick={handleUpload}
            disabled={!parsed || !bulkSaveS3Link?.trim() || isUploading || isProcessing || bulkSaveLoading}
            loading={isUploading || bulkSaveLoading}
            className="bg-hero-red hover:bg-hero-red-60 text-white min-w-[120px] disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              {isUploading || bulkSaveLoading ? 'Saving…' : 'Load Data'}
            </div>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
